import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Play, X, Volume2, VolumeX, MessageCircle, Star } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { reelContact } from "@/lib/site";
import { REELS_DEFAULTS } from "@/lib/sectionVariants";
import { SectionShell } from "@/components/site/primitives";
import AutoCarousel from "@/components/site/AutoCarousel";
import SmartImage from "@/components/site/SmartImage";

export const ASPECT = { "9:16": "aspect-[9/16]", "1:1": "aspect-square", "16:9": "aspect-video" };
const WIDTHS = {
  "9:16": { sm: "w-[110px]", md: "w-[150px]", lg: "w-[200px]" },
  "1:1": { sm: "w-[130px]", md: "w-[170px]", lg: "w-[230px]" },
  "16:9": { sm: "w-[200px]", md: "w-[260px]", lg: "w-[330px]" },
};
const GRID = {
  sm: "grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3",
  md: "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3",
  lg: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4",
};

export const useReelsData = (params = "") => {
  const [reels, setReels] = useState([]);
  const [bizMap, setBizMap] = useState({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    Promise.all([api.get(`/public/reels${params}`), api.get("/public/businesses")])
      .then(([r, b]) => {
        if (!alive) return;
        const map = {};
        b.data.forEach((x) => (map[x.id] = x));
        setReels(r.data.filter((x) => x.video));
        setBizMap(map);
      })
      .catch((e) => console.error("Erro ao carregar reels", e))
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [params]);
  return { reels, bizMap, loaded };
};

export const ReelThumb = ({ r, biz, onOpen, aspect = "aspect-[9/16]", showTitle = true, preview = false }) => (
  <button type="button" onClick={onOpen} data-testid={`reel-thumb-${r.id}`}
    className={`group relative block w-full ${aspect} rounded-2xl overflow-hidden bg-slate-900 text-left`}>
    {preview || !r.thumbnail ? (
      <video src={preview ? fileUrl(r.video) : `${fileUrl(r.video)}#t=0.5`} poster={r.thumbnail ? fileUrl(r.thumbnail) : undefined}
        muted playsInline preload="metadata" autoPlay={preview} loop={preview}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
    ) : (
      <SmartImage src={r.thumbnail} alt={r.title || ""} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
    {r.featured && (
      <span className="absolute top-2 left-2 flex items-center gap-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full px-2 py-0.5">
        <Star className="w-3 h-3" /> Destaque
      </span>
    )}
    <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 backdrop-blur flex items-center justify-center">
      <Play className="w-3.5 h-3.5 text-white fill-white" />
    </span>
    {showTitle && (
      <div className="absolute bottom-0 inset-x-0 p-2.5 text-white">
        {r.title && <p className="text-xs sm:text-sm font-bold leading-tight line-clamp-2">{r.title}</p>}
        {biz && <p className="text-[10px] sm:text-xs text-white/75 line-clamp-1 mt-0.5">{biz.name}</p>}
      </div>
    )}
  </button>
);

const ReelSlide = ({ r, biz, onClose }) => {
  const cta = reelContact(r, biz);
  const toggle = (e) => { const v = e.currentTarget; if (v.paused) v.play().catch(() => {}); else v.pause(); };
  return (
    <div className="relative h-[100dvh] w-full snap-start flex items-center justify-center" data-testid={`reel-slide-${r.id}`}>
      <video src={fileUrl(r.video)} poster={r.thumbnail ? fileUrl(r.thumbnail) : undefined} loop playsInline preload="metadata" onClick={toggle}
        className="h-full w-full sm:w-auto sm:aspect-[9/16] object-cover sm:rounded-2xl bg-black" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 to-transparent sm:mx-auto sm:max-w-[56.25dvh]" />
      <div className="absolute inset-x-0 bottom-0 p-4 pb-8 sm:mx-auto sm:max-w-[56.25dvh] text-white">
        {biz && (
          <Link to={`/empresa/${biz.id}`} onClick={onClose} data-testid={`reel-biz-${r.id}`} className="inline-flex items-center gap-2 mb-2">
            <SmartImage src={biz.logo || biz.cover} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white" iconClassName="w-4 h-4" />
            <span className="text-sm font-bold">{biz.name}</span>
          </Link>
        )}
        {r.title && <h3 className="text-base font-bold font-heading leading-tight">{r.title}</h3>}
        {r.description && <p className="text-sm text-white/85 mt-1 line-clamp-3 whitespace-pre-line">{r.description}</p>}
        {cta && r.button_enabled !== false && (
          <a href={cta} target="_blank" rel="noreferrer" data-testid={`reel-cta-${r.id}`}
            className="mt-3 inline-flex items-center gap-2 bg-secondary text-secondary-foreground text-sm font-bold rounded-full px-5 py-2.5 active:scale-95 transition-transform">
            <MessageCircle className="w-4 h-4" /> {r.button_text || "Falar com a empresa"}
          </a>
        )}
      </div>
    </div>
  );
};

export const ReelViewer = ({ reels, start = 0, bizMap = {}, onClose }) => {
  const listRef = useRef(null);
  const closeRef = useRef(onClose);
  const [muted, setMuted] = useState(false);
  closeRef.current = onClose;

  useEffect(() => {
    const el = listRef.current;
    const child = el?.children[start];
    if (child) el.scrollTop = child.offsetTop;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") closeRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [start]);

  useEffect(() => {
    const el = listRef.current;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const v = en.target;
        if (en.isIntersecting) {
          v.play().catch(() => { v.muted = true; setMuted(true); v.play().catch(() => {}); });
        } else {
          v.pause();
        }
      });
    }, { root: el, threshold: 0.6 });
    el.querySelectorAll("video").forEach((v) => io.observe(v));
    return () => io.disconnect();
  }, [reels]);

  useEffect(() => {
    listRef.current?.querySelectorAll("video").forEach((v) => { v.muted = muted; });
  }, [muted]);

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black" data-testid="reel-viewer">
      <div ref={listRef} className="h-[100dvh] overflow-y-scroll snap-y snap-mandatory hide-scrollbar">
        {reels.map((r) => <ReelSlide key={r.id} r={r} biz={bizMap[r.business_id]} onClose={() => closeRef.current()} />)}
      </div>
      <div className="absolute top-4 right-4 flex gap-2">
        <button type="button" onClick={() => setMuted((m) => !m)} data-testid="reel-mute-toggle" aria-label="Som"
          className="w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/25 transition-colors">
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        <button type="button" onClick={() => closeRef.current()} data-testid="reel-close" aria-label="Fechar"
          className="w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/25 transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>,
    document.body
  );
};

const sortReels = (list, sort) => {
  const arr = [...list];
  if (sort === "recent") arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  else if (sort === "featured") arr.sort((a, b) => Number(!!b.featured) - Number(!!a.featured));
  else if (sort === "random") arr.sort(() => Math.random() - 0.5);
  return arr;
};

export const ReelsSection = ({ section }) => {
  const { reels, bizMap } = useReelsData();
  const [open, setOpen] = useState(null);
  const cfg = { ...REELS_DEFAULTS, ...section };
  const list = useMemo(() => {
    const base = cfg.featured_only ? reels.filter((r) => r.featured) : reels;
    return sortReels(base, cfg.sort).slice(0, Math.max(1, Number(cfg.count) || 10));
  }, [reels, cfg.featured_only, cfg.sort, cfg.count]);
  if (!list.length) return null;

  const aspect = ASPECT[cfg.format] || ASPECT["9:16"];
  const width = (WIDTHS[cfg.format] || WIDTHS["9:16"])[cfg.thumb_size] || WIDTHS["9:16"].md;
  const thumb = (r, i) => <ReelThumb r={r} biz={bizMap[r.business_id]} onOpen={() => setOpen(i)} aspect={aspect} showTitle={cfg.show_titles} preview={cfg.autoplay_preview} />;

  return (
    <SectionShell title={section.title || "Reels"} id={section.key} viewAllTo="/reels">
      {cfg.variant === "grid" ? (
        <div className={GRID[cfg.thumb_size] || GRID.md}>{list.map((r, i) => <div key={r.id}>{thumb(r, i)}</div>)}</div>
      ) : (
        <AutoCarousel section={section} testId={`dots-${section.key}`}>{list.map((r, i) => <div key={r.id} className={`snap-item flex-none ${width}`}>{thumb(r, i)}</div>)}</AutoCarousel>
      )}
      {open !== null && <ReelViewer reels={list} start={open} bizMap={bizMap} onClose={() => setOpen(null)} />}
    </SectionShell>
  );
};

export const ReelsGrid = ({ reels, bizMap, size = "md" }) => {
  const [open, setOpen] = useState(null);
  return (
    <>
      <div className={GRID[size] || GRID.md} data-testid="reels-grid">
        {reels.map((r, i) => <div key={r.id}><ReelThumb r={r} biz={bizMap[r.business_id]} onOpen={() => setOpen(i)} /></div>)}
      </div>
      {open !== null && <ReelViewer reels={reels} start={open} bizMap={bizMap} onClose={() => setOpen(null)} />}
    </>
  );
};
